import { useState, useMemo, useCallback } from "react";

/**
 * useFilters Hook
 * Reusable filtering logic for lists with search and structured filters
 * 
 * @param {Array} data - Array of items to filter
 * @param {Object} config - Configuration object
 * @param {Array} config.searchFields - Fields to search in (e.g., ['name', 'email'])
 * @param {Object} config.filterFields - Filter field configs (e.g., { status: 'status', type: 'clientType' })
 * @returns {Object} - Filter state, setters, and filtered data
 */
export function useFilters(data = [], config = {}) {
  const { searchFields = [], filterFields = {}, defaultFilters = {} } = config;
  
  // Search state
  const [searchQuery, setSearchQuery] = useState("");
  
  // Dynamic filter state - one state object for all filters
  // Apply defaultFilters if provided, otherwise use "all"
  const [filters, setFilters] = useState(
    Object.keys(filterFields).reduce((acc, key) => ({ 
      ...acc, 
      [key]: defaultFilters[key] || "all" 
    }), {})
  );
  
  // Date range state
  const [dateRange, setDateRange] = useState({ from: null, to: null });

  // Set individual filter
  const setFilter = useCallback((filterKey, value) => {
    setFilters(prev => ({ ...prev, [filterKey]: value }));
  }, []);

  // Reset all filters (back to defaults, not "all")
  const resetFilters = useCallback(() => {
    setSearchQuery("");
    setFilters(
      Object.keys(filterFields).reduce((acc, key) => ({ 
        ...acc, 
        [key]: defaultFilters[key] || "all" 
      }), {})
    );
    setDateRange({ from: null, to: null });
  }, [filterFields, defaultFilters]);

  // Check if any filter is active
  const hasActiveFilters = useMemo(() => {
    const hasSearch = searchQuery.trim() !== "";
    const hasFilters = Object.values(filters).some(v => v !== "all");
    const hasDateRange = dateRange.from || dateRange.to;
    return hasSearch || hasFilters || hasDateRange;
  }, [searchQuery, filters, dateRange]);

  // Filtered data
  const filteredData = useMemo(() => {
    let result = [...data];

    // Apply search
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      result = result.filter(item => {
        return searchFields.some(field => {
          const value = getNestedValue(item, field);
          if (value === null || value === undefined) return false;
          return String(value).toLowerCase().includes(query);
        });
      });
    }

    // Apply structured filters
    Object.entries(filters).forEach(([filterKey, filterValue]) => {
      if (filterValue !== "all") {
        const fieldPath = filterFields[filterKey];
        result = result.filter(item => {
          const value = getNestedValue(item, fieldPath);
          return value === filterValue;
        });
      }
    });

    // Apply date range filter (looks for 'date' field or configured dateField)
    if (dateRange.from || dateRange.to) {
      const dateField = config.dateField || "date";
      result = result.filter(item => {
        const itemDate = new Date(getNestedValue(item, dateField));
        if (isNaN(itemDate.getTime())) return true; // Keep items without valid dates
        
        if (dateRange.from && itemDate < new Date(dateRange.from)) return false;
        if (dateRange.to) {
          const toDate = new Date(dateRange.to);
          toDate.setHours(23, 59, 59, 999);
          if (itemDate > toDate) return false;
        }
        return true;
      });
    }

    return result;
  }, [data, searchQuery, filters, dateRange, searchFields, filterFields, config.dateField]);

  return {
    // Search
    searchQuery,
    setSearchQuery,
    
    // Filters
    filters,
    setFilter,
    setFilters,
    
    // Date range
    dateRange,
    setDateRange,
    
    // Utilities
    resetFilters,
    hasActiveFilters,
    
    // Results
    filteredData,
    totalCount: data.length,
    filteredCount: filteredData.length
  };
}

/**
 * Get nested value from object using dot notation
 * e.g., getNestedValue(obj, 'user.name') returns obj.user.name
 */
function getNestedValue(obj, path) {
  if (!path) return obj;
  return path.split('.').reduce((acc, part) => acc?.[part], obj);
}

export default useFilters;
